import React, { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert, Paper } from '@mui/material';
import { ContentCopy as CopyIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

interface QRCodeDisplayProps {
  url: string;
  title: string;
  size?: number;
  fillSpace?: boolean;
}

export const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  url,
  title,
  size = 256,
  fillSpace = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [qrSize, setQrSize] = useState(size);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (fillSpace && containerRef.current) {
      const updateSize = () => {
        const container = containerRef.current;
        if (container) {
          const rect = container.getBoundingClientRect();
          // Get viewport height for proper calculation
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;

          // Calculate reserved space for other elements (more accurate)
          // Title: ~40px, URL box: ~70px, Button: ~50px, Padding: ~40px
          const reservedHeight = 200;
          const reservedWidth = 60; // Side padding

          // Calculate available space - use most of the viewport for large displays
          const availableHeight = Math.min(
            rect.height - reservedHeight,
            viewportHeight * 0.75 // Use up to 75% of viewport for QR on large screens
          );
          const availableWidth = Math.min(
            rect.width - reservedWidth,
            viewportWidth * 0.9 // Use up to 90% of viewport width
          );

          // Set bounds optimized for large displays
          // Much higher cap for presentation screens
          const maxSize = Math.min(availableHeight, availableWidth, 1200); // Cap at 1200px for huge screens
          const newSize = Math.max(maxSize, 200); // Minimum 200px

          // Only update if size actually changed to prevent unnecessary re-renders
          setQrSize((prevSize) => {
            const roundedSize = Math.round(newSize);
            return prevSize === roundedSize ? prevSize : roundedSize;
          });
        }
      };

      // Use ResizeObserver for smooth updates
      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(containerRef.current);

      // Initial size calculation with slight delay to ensure DOM is ready
      setTimeout(updateSize, 0);

      // Also update on window resize
      window.addEventListener('resize', updateSize);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateSize);
      };
    } else {
      setQrSize(size);
    }
  }, [fillSpace, size]);

  const handleCopy = () => {
    setCopied(true);
  };

  const handleSnackbarClose = () => {
    setCopied(false);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: fillSpace ? 1.5 : 3,
        width: '100%',
        height: fillSpace ? '100%' : 'auto',
        justifyContent: fillSpace ? 'center' : 'flex-start',
        maxHeight: fillSpace ? 'calc(100vh - 120px)' : 'none', // Less reserved space
        overflow: 'auto',
        padding: fillSpace ? 1 : 0, // Less padding to maximize QR space
      }}
    >
      {/* Title */}
      <Typography
        variant={fillSpace ? 'h6' : 'h5'}
        component="h2"
        sx={{
          fontWeight: 'semibold',
          color: 'text.primary',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {title}
      </Typography>

      {/* QR Code */}
      <Paper
        elevation={2}
        sx={{
          p: fillSpace ? 1.5 : 3, // Less padding when filling space
          borderRadius: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          maxWidth: '95%', // Allow more width usage
          maxHeight: '80vh', // Prevent vertical overflow
        }}
      >
        <QRCodeSVG
          value={url}
          size={qrSize}
          style={{
            height: 'auto',
            maxWidth: '100%',
            width: '100%',
            display: 'block',
          }}
        />
      </Paper>

      {/* URL Display */}
      <Box
        sx={{
          p: fillSpace ? 1 : 2, // Compact padding when filling space
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: '95%',
          wordBreak: 'break-all',
          flexShrink: 0,
        }}
      >
        <Typography
          variant={fillSpace ? 'caption' : 'body2'} // Smaller text when filling space
          sx={{
            fontFamily: 'monospace',
            color: 'text.secondary',
            fontSize: fillSpace ? '0.75rem' : undefined,
          }}
        >
          {url}
        </Typography>
      </Box>

      {/* Copy Button */}
      <CopyToClipboard text={url} onCopy={handleCopy}>
        <Button
          variant="contained"
          size={fillSpace ? 'medium' : 'large'} // Smaller button when filling space
          startIcon={copied ? <CheckIcon /> : <CopyIcon />}
          color={copied ? 'success' : 'primary'}
          sx={{
            px: fillSpace ? 3 : 4,
            py: fillSpace ? 1 : 1.5,
            fontSize: fillSpace ? '1rem' : '1.1rem',
            fontWeight: 'semibold',
            borderRadius: 2,
          }}
        >
          {copied ? 'Link Copied!' : 'Copy Link'}
        </Button>
      </CopyToClipboard>

      {/* Success Snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};
